import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gmailApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Mail,
    MailCheck,
    MailX,
    ExternalLink,
    Loader2,
    RefreshCw,
    Unplug,
    Check,
    X,
    AlertCircle
} from 'lucide-react';

export default function GmailPage() {
    const { user, refreshUser } = useAuth();
    const [searchParams] = useSearchParams();
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [approvalData, setApprovalData] = useState({
        cliente: '',
        nome_progetto: '',
        tipologia_progetto: '',
        citta: '',
        architetto: '',
        data_prevista_consegna: null,
        quantita: '',
        serie: '',
        colore: '',
        formato: '',
        finitura: '',
        prezzo_confermato: '',
        note: ''
    });
    const [processing, setProcessing] = useState(false);

    // Handle OAuth callback
    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        
        if (code && state) {
            handleOAuthCallback(code, state);
        }
    }, [searchParams]);

    const handleOAuthCallback = async (code, state) => {
        try {
            setConnecting(true);
            await gmailApi.callback(code, state);
            toast.success('Gmail connesso con successo!');
            refreshUser();
            // Clear URL params
            window.history.replaceState({}, document.title, '/gmail');
            fetchEmails();
        } catch (error) {
            toast.error('Errore durante la connessione a Gmail');
        } finally {
            setConnecting(false);
        }
    };

    useEffect(() => {
        if (user?.gmail_connected) {
            fetchEmails();
        }
    }, [user?.gmail_connected]);

    const fetchEmails = async () => {
        setLoading(true);
        try {
            const response = await gmailApi.getEmails();
            setEmails(response.data);
        } catch (error) {
            if (error.response?.status !== 400) {
                toast.error('Errore nel caricamento delle email');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConnectGmail = async () => {
        try {
            setConnecting(true);
            const response = await gmailApi.getAuthUrl();
            window.location.href = response.data.authorization_url;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore durante la connessione');
            setConnecting(false);
        }
    };

    const handleDisconnectGmail = async () => {
        try {
            await gmailApi.disconnect();
            toast.success('Gmail disconnesso');
            refreshUser();
            setEmails([]);
        } catch (error) {
            toast.error('Errore durante la disconnessione');
        }
    };

    const openApprovalDialog = (email) => {
        setSelectedEmail(email);
        // Pre-fill with email content extraction (basic)
        setApprovalData({
            cliente: '',
            nome_progetto: email.subject || '',
            tipologia_progetto: '',
            citta: '',
            architetto: '',
            data_prevista_consegna: null,
            quantita: '',
            serie: '',
            colore: '',
            formato: '',
            finitura: '',
            prezzo_confermato: '',
            note: `Importato da email di: ${email.sender}\n\n${email.snippet}`
        });
    };

    const handleApprove = async () => {
        if (!approvalData.cliente || !approvalData.nome_progetto) {
            toast.error('Cliente e Nome Progetto sono obbligatori');
            return;
        }

        setProcessing(true);
        try {
            await gmailApi.approveEmail(selectedEmail.id, {
                approved: true,
                project_data: approvalData
            });
            toast.success('Progetto creato da email');
            setSelectedEmail(null);
            fetchEmails();
        } catch (error) {
            toast.error('Errore durante l\'approvazione');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async (emailId) => {
        try {
            await gmailApi.approveEmail(emailId, { approved: false });
            toast.success('Email rifiutata');
            fetchEmails();
        } catch (error) {
            toast.error('Errore durante il rifiuto');
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <Badge className="bg-emerald-100 text-emerald-800">Approvata</Badge>;
            case 'rejected':
                return <Badge className="bg-red-100 text-red-800">Rifiutata</Badge>;
            default:
                return <Badge className="bg-amber-100 text-amber-800">In attesa</Badge>;
        }
    };

    // Gmail not configured message
    const gmailNotConfigured = !user?.gmail_connected && (
        <Card className="bg-white border-slate-200 rounded-sm">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Mail className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    Connetti Gmail
                </h3>
                <p className="text-sm text-slate-500 max-w-md mb-6">
                    Collega il tuo account Gmail per importare automaticamente le richieste di progetto dalle email.
                    Potrai revisionare e approvare ogni email prima che venga convertita in un progetto.
                </p>
                
                <Button
                    onClick={handleConnectGmail}
                    disabled={connecting}
                    className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                    data-testid="connect-gmail-btn"
                >
                    {connecting ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connessione...
                        </>
                    ) : (
                        <>
                            <Mail className="w-4 h-4 mr-2" />
                            Connetti Gmail
                        </>
                    )}
                </Button>
            </CardContent>
        </Card>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Email
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {user?.gmail_connected 
                            ? 'Rivedi le email per creare nuovi progetti'
                            : 'Collega Gmail per importare progetti dalle email'
                        }
                    </p>
                </div>
                {user?.gmail_connected && (
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            onClick={fetchEmails}
                            disabled={loading}
                            className="rounded-sm"
                            data-testid="refresh-emails-btn"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Aggiorna
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleDisconnectGmail}
                            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="disconnect-gmail-btn"
                        >
                            <Unplug className="w-4 h-4 mr-2" />
                            Disconnetti
                        </Button>
                    </div>
                )}
            </div>

            {/* Content */}
            {!user?.gmail_connected ? (
                gmailNotConfigured
            ) : loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
            ) : emails.length === 0 ? (
                <Card className="bg-white border-slate-200 rounded-sm">
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <MailCheck className="w-12 h-12 text-slate-300 mb-4" />
                        <h3 className="text-lg font-semibold text-slate-700">Nessuna email da processare</h3>
                        <p className="text-sm text-slate-500 mt-1">
                            Le nuove email non lette appariranno qui
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {emails.map((email) => (
                        <Card 
                            key={email.id} 
                            className="bg-white border-slate-200 rounded-sm hover:border-slate-300 transition-colors"
                            data-testid={`email-card-${email.id}`}
                        >
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            {getStatusBadge(email.status)}
                                            <span className="text-xs text-slate-500 font-mono">
                                                {new Date(email.received_at).toLocaleDateString('it-IT')}
                                            </span>
                                        </div>
                                        <CardTitle className="text-base font-semibold text-slate-900 truncate">
                                            {email.subject}
                                        </CardTitle>
                                        <CardDescription className="text-sm text-slate-500 truncate">
                                            Da: {email.sender}
                                        </CardDescription>
                                    </div>
                                    {email.status === 'pending' && (
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Button
                                                size="sm"
                                                onClick={() => openApprovalDialog(email)}
                                                className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
                                                data-testid={`approve-email-${email.id}`}
                                            >
                                                <Check className="w-4 h-4 mr-1" />
                                                Approva
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleReject(email.id)}
                                                className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                                                data-testid={`reject-email-${email.id}`}
                                            >
                                                <X className="w-4 h-4 mr-1" />
                                                Rifiuta
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-slate-600 line-clamp-2">
                                    {email.snippet}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Approval Dialog */}
            <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Crea Progetto da Email
                        </DialogTitle>
                        <DialogDescription>
                            Compila i campi per creare un nuovo progetto da questa email
                        </DialogDescription>
                    </DialogHeader>

                    {selectedEmail && (
                        <div className="mb-4 p-4 bg-slate-50 rounded-sm border border-slate-200">
                            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                                Email originale
                            </p>
                            <p className="text-sm font-medium text-slate-900">{selectedEmail.subject}</p>
                            <p className="text-xs text-slate-500">Da: {selectedEmail.sender}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Cliente *
                            </Label>
                            <Input
                                value={approvalData.cliente}
                                onChange={(e) => setApprovalData({ ...approvalData, cliente: e.target.value })}
                                placeholder="Nome cliente"
                                className="rounded-sm"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Nome Progetto *
                            </Label>
                            <Input
                                value={approvalData.nome_progetto}
                                onChange={(e) => setApprovalData({ ...approvalData, nome_progetto: e.target.value })}
                                placeholder="Nome del progetto"
                                className="rounded-sm"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Tipologia
                            </Label>
                            <Input
                                value={approvalData.tipologia_progetto}
                                onChange={(e) => setApprovalData({ ...approvalData, tipologia_progetto: e.target.value })}
                                placeholder="Es: Residenziale"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Città
                            </Label>
                            <Input
                                value={approvalData.citta}
                                onChange={(e) => setApprovalData({ ...approvalData, citta: e.target.value })}
                                placeholder="Città"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Architetto
                            </Label>
                            <Input
                                value={approvalData.architetto}
                                onChange={(e) => setApprovalData({ ...approvalData, architetto: e.target.value })}
                                placeholder="Nome architetto"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Quantità
                            </Label>
                            <Input
                                value={approvalData.quantita}
                                onChange={(e) => setApprovalData({ ...approvalData, quantita: e.target.value })}
                                placeholder="Es: 100 mq"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Serie
                            </Label>
                            <Input
                                value={approvalData.serie}
                                onChange={(e) => setApprovalData({ ...approvalData, serie: e.target.value })}
                                placeholder="Serie"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Colore
                            </Label>
                            <Input
                                value={approvalData.colore}
                                onChange={(e) => setApprovalData({ ...approvalData, colore: e.target.value })}
                                placeholder="Colore"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Formato
                            </Label>
                            <Input
                                value={approvalData.formato}
                                onChange={(e) => setApprovalData({ ...approvalData, formato: e.target.value })}
                                placeholder="Es: 60x120"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Finitura
                            </Label>
                            <Input
                                value={approvalData.finitura}
                                onChange={(e) => setApprovalData({ ...approvalData, finitura: e.target.value })}
                                placeholder="Es: Opaco"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Prezzo Confermato
                            </Label>
                            <Input
                                value={approvalData.prezzo_confermato}
                                onChange={(e) => setApprovalData({ ...approvalData, prezzo_confermato: e.target.value })}
                                placeholder="Es: €25/mq"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Note
                            </Label>
                            <Textarea
                                value={approvalData.note}
                                onChange={(e) => setApprovalData({ ...approvalData, note: e.target.value })}
                                placeholder="Note aggiuntive..."
                                className="rounded-sm min-h-[100px]"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedEmail(null)}
                            className="rounded-sm"
                        >
                            Annulla
                        </Button>
                        <Button
                            onClick={handleApprove}
                            className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
                            disabled={processing}
                            data-testid="confirm-approve-btn"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Creazione...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Crea Progetto
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
